import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../SEO';
import { appConfig } from '../../config/appConfig';
import cleaningHeroVisual from '../../images/Comp_4.gif';
import operationsVisual from '../../images/dashboard.png';
import workforceVisual from '../../images/Comp_2.gif';

const faqs = [
  {
    question: 'What multiples are commercial cleaning platforms trading at today?',
    answer:
      'Scaled commercial cleaning and facilities services platforms with 20%+ adjusted EBITDA margins, diversified contract portfolios, and tech-enabled routing routinely clear 7-9x EBITDA. Regional operators with mission-critical verticals (healthcare, aviation, education) and bundled soft services can command double-digit multiples.',
  },
  {
    question: 'How much contract concentration is acceptable to buyers?',
    answer:
      'Most institutional buyers are comfortable up to 25% revenue concentration in a single client when contracts include multi-year terms, escalation language, and embedded compliance requirements. We prepare contract scorecards that highlight renewal history, termination clauses, and cross-sell penetration to de-risk the story.',
  },
  {
    question: 'Do I need audited financials before launching a process?',
    answer:
      'Audits are helpful but not mandatory. We normalize cash- and accrual-basis books, rebuild revenue by contract, and align labor burden and material pass-through so QoE firms can move quickly. This “pre-QoE” work keeps diligence focused on upside rather than reconciliation.',
  },
  {
    question: 'What happens to my leadership team after a sale?',
    answer:
      'Buyers expect continuity for key leadership roles 12-24 months post-close. We negotiate retention programs, earn-out structures tied to contract expansion, and post-close autonomy so operations leaders, regional managers, and union stewards remain motivated.',
  },
];

const structuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Commercial Cleaning & Facilities Services M&A Advisory',
    name: 'Commercial Cleaning & Facilities Services M&A Advisor',
    provider: {
      '@type': 'Organization',
      name: appConfig.site.organizationName,
      url: appConfig.site.baseUrl,
    },
    areaServed: ['United States', 'Canada'],
    audience: {
      '@type': 'Audience',
      name: 'Owners of commercial cleaning, janitorial, and facilities services companies',
    },
    url: `${appConfig.site.baseUrl}/commercial-cleaning-mna-advisor`,
    description:
      'Sell-side advisory for commercial cleaning and facilities services operators. AMIVA packages contract portfolios, compliance records, and technician productivity into investor-ready narratives.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: `${appConfig.site.baseUrl}/valuation`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  },
];

const heroMetrics = [
  {
    label: 'Active Facilities Buyers',
    value: '95+',
    description: 'commercial services strategics and PE platforms pursuing janitorial/FMS assets.',
  },
  {
    label: 'Contracted Revenue Mix',
    value: '60%',
    description: 'median recurring backlog captured pre-process across AMIVA cleaning mandates.',
  },
  {
    label: 'Readiness Sprint',
    value: '5 weeks',
    description: 'time to normalize contract P&L, workforce metrics, and compliance evidence.',
  },
];

const visualShowcase = [
  {
    image: cleaningHeroVisual,
    alt: 'Commercial cleaning supervisors reviewing a service dashboard.',
    label: 'Service Footprint',
    metric: 'Multi-site programs',
    caption: 'Interactive maps and routing visuals demonstrate coverage and dispatch efficiency.',
    gradient: 'from-[#0B192E]/70 via-[#2563EB]/60 to-[#38BDF8]/60',
  },
  {
    image: operationsVisual,
    alt: 'Facilities services KPI dashboard showing contract health.',
    label: 'Contract Health',
    metric: '98% retention',
    caption: 'Contract scorecards surface renewal cadence, escalation clauses, and margin profile.',
    gradient: 'from-[#12274A]/70 via-[#1B3A70]/60 to-[#9333EA]/60',
  },
  {
    image: workforceVisual,
    alt: 'Workforce planning board for janitorial crews.',
    label: 'Workforce Pulse',
    metric: '87% retention',
    caption: 'Labor dashboards highlight tenure, safety recordables, and training completion rates.',
    gradient: 'from-[#0F172A]/70 via-[#10B981]/60 to-[#F59E0B]/60',
  },
];

const highlightStats = [
  {
    label: 'Days to Multiple LOIs',
    value: '24',
    detail: 'average timeframe to secure 3+ offers once contract data room launches.',
  },
  {
    label: 'Cross-Sell Uplift',
    value: '32%',
    detail: 'buyer-modeled incremental EBITDA from bundling soft services and ESG programs.',
  },
  {
    label: 'Cash at Close',
    value: '72%',
    detail: 'median upfront consideration with tailored rollover for growth capital.',
  },
];

const readinessChecklist = [
  'Compile contract roster with start/end dates, termination clauses, and escalation language.',
  'Segment revenue by vertical (Class A office, healthcare, education, industrial, retail).',
  'Document compliance stack: OSHA logs, SDS records, union agreements, background checks.',
  'Map labor burden, overtime management, and subcontractor usage by route/shift.',
  'Provide quality inspection scores, client satisfaction surveys, and complaint resolution cadence.',
  'Detail equipment fleet, CAPEX history, and green cleaning/ESG initiatives.',
  'Summarize technology stack: work order, scheduling, IoT sensors, and reporting portals.',
  'Highlight cross-sell penetration (disinfection, day porter, waste, security, landscaping).',
];

const serviceSignals = [
  {
    title: 'Contract Stability Layer',
    summary: 'Heat maps show renewal probability, escalation indexation, and compliance audits.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h10" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 17l4 4m0 0l4-4m-4 4V3" />
      </svg>
    ),
  },
  {
    title: 'Route Productivity Pulse',
    summary: 'Net hour per KSF, overtime usage, and drive time benchmarks per geography.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M3 6h18M3 18h10" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 18l3 3 3-3" />
      </svg>
    ),
  },
  {
    title: 'Workforce Safety Tracker',
    summary: 'TRIR, lost time incidents, training cadence, and credential compliance summarized.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12M6 10l6-4 6 4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 14l6 4 6-4" />
      </svg>
    ),
  },
];

const personaProfiles = [
  {
    title: 'Multi-Market Contract Platforms',
    stat: 'National coverage',
    description:
      'Operators managing Class A office, healthcare, and public space portfolios with centralized compliance and data rooms ready for institutional diligence.',
  },
  {
    title: 'Soft Services Bundles',
    stat: 'Integrated FM',
    description:
      'Cleaning plus security, landscaping, or maintenance combinations seeking buyers that value bundled contracts and cross-sell penetration.',
  },
  {
    title: 'Founder Succession & ESOP Rollovers',
    stat: 'Hybrid liquidity',
    description:
      'Family-founded or employee-owned firms planning leadership transitions while retaining equity for the next chapter of growth.',
  },
];

const processHighlights = [
  {
    title: 'Readiness Sprint',
    timeline: 'Weeks 1-2',
    items: ['Contract roster cleanup', 'Route profitability rebuild', 'Compliance evidence vault'],
  },
  {
    title: 'Buyer Activation',
    timeline: 'Weeks 3-6',
    items: ['Localized outreach waves', 'Tech-enabled walkthroughs', 'Weekly offer board reviews'],
  },
  {
    title: 'Offer Alignment',
    timeline: 'Weeks 7-10',
    items: ['Structure modeling', 'Leadership retention plans', 'Integration runway workshops'],
  },
];

const relatedPlaybooks = [
  {
    title: 'HVAC & Home Services M&A Advisor',
    description: 'Recurring service plans, dispatch efficiency, and technician retention narratives.',
    to: '/hvac-home-services-mna-advisor',
  },
  {
    title: 'Manufacturing & Precision Machining M&A',
    description: 'Operational excellence proof for industrial buyers seeking facilities adjacencies.',
    to: '/manufacturing-precision-machining-mna',
  },
  {
    title: 'Cross-Border M&A Advisory',
    description: 'Coordinate US-EU buyer waves for facilities platforms expanding internationally.',
    to: '/cross-border-mna-us-europe',
  },
];

export default function CommercialCleaningAdvisor() {
  return (
    <div className="bg-white">
      <SEOHead
        title="Commercial Cleaning & Facilities Services M&A Advisor | AMIVA Equity Partners"
        description="AMIVA crafts investor-grade narratives for commercial cleaning and facilities services operators. Package contract stability, workforce performance, and compliance proof to secure premium outcomes."
        keywords={[
          'commercial cleaning M&A advisor',
          'facilities services sell-side',
          'janitorial company valuation',
          'sell my commercial cleaning business',
        ]}
        canonical="/commercial-cleaning-mna-advisor"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Industries', path: '/industries' },
          { name: 'Commercial Cleaning & Facilities Services', path: '/commercial-cleaning-mna-advisor' },
        ]}
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B192E] via-[#12274A] to-[#1B3A70] text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-[-6rem] -left-28 w-[28rem] h-[28rem] bg-amiva-primary/45 rounded-full blur-3xl"></div>
          <div className="absolute bottom-[-6rem] right-[-4rem] w-[30rem] h-[30rem] bg-amiva-accent/35 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,_3fr)_minmax(0,_2.4fr)] gap-12 items-center">
            <div className="max-w-3xl">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-6">
                Commercial Cleaning M&A Advisor
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                <span className="text-white">Turn contract portfolios into premium facilities outcomes</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-200 leading-relaxed mb-6">
                We translate contract durability, workforce performance, and compliance leadership into
                the language institutional buyers expect. Founders stay focused on service delivery while
                we orchestrate buyer waves, negotiate structure, and protect your brand equity.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {heroMetrics.map((metric) => (
                  <div key={metric.label} className="rounded-2xl border border-white/15 bg-white/5 p-4 text-center">
                    <div className="text-2xl font-semibold text-white">{metric.value}</div>
                    <p className="mt-1 text-xs uppercase tracking-[0.28em] text-white/70">{metric.label}</p>
                    <p className="mt-2 text-[11px] leading-relaxed text-white/60">{metric.description}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Link to="/valuation" className="btn-primary px-8 py-4 text-lg font-semibold">
                  Benchmark my company
                </Link>
                <a
                  href={appConfig.links.calendly}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-base font-semibold text-white hover:text-amiva-accent transition-colors"
                >
                  Schedule a confidential call →
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-12 right-0 h-24 w-24 rounded-full bg-amiva-accent/40 blur-xl"></div>
              <div className="absolute bottom-[-2rem] -left-10 h-20 w-20 rounded-full bg-white/20 blur-lg"></div>
              <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/5 shadow-[0_30px_60px_rgba(11,25,46,0.45)]">
                <img
                  src={cleaningHeroVisual}
                  alt="Commercial cleaning leadership team reviewing contract dashboards."
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#12274A]/35 via-[#1B3A70]/10 to-transparent"></div>
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1 text-xs uppercase tracking-[0.32em] text-white">
                  Contract runway mapped
                </div>
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                  <p className="text-sm font-semibold text-white">
                    $4.8M incremental EBITDA identified through contract escalators & cross-sell bundling
                  </p>
                  <p className="mt-1 text-xs text-white/70">
                    Data room assets align with buyer underwriting, enabling faster diligence cycles.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Profiles */}
      <section className="py-16 lg:py-20 bg-gray-50" id="founder-profiles">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Founder Snapshot
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Facilities services leaders we guide through premium exits
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Processes are tailored to your contract mix, workforce model, and expansion agenda. These
              archetypes highlight how the playbook flexes for each founder profile.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {personaProfiles.map((profile) => (
              <div
                key={profile.title}
                className="rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  {profile.stat}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{profile.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{profile.description}</p>
                <div className="text-xs text-gray-500">
                  We negotiate rollover, retention, and integration guardrails before exclusivity.
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Story */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Contract Storyboards
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Show buyers the contract, workforce, and compliance story in minutes
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Visual dashboards replace dense binders. Buyers grasp contract durability, workforce capacity,
              and technology leverage without multiple conference calls.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visualShowcase.map((shot) => (
              <div
                key={shot.label}
                className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-gray-900/5 shadow-lg"
              >
                <img
                  src={shot.image}
                  alt={shot.alt}
                  className="h-60 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className={`absolute inset-0 bg-gradient-to-br ${shot.gradient} opacity-80 mix-blend-multiply`}></div>
                <div className="absolute inset-0 flex flex-col justify-between p-6">
                  <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/15 px-3 py-1 text-xs uppercase tracking-[0.28em] text-white">
                    {shot.label}
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
                    <div className="text-sm font-semibold text-white">{shot.metric}</div>
                    <p className="mt-1 text-xs text-white/75 leading-relaxed">{shot.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-gradient p-8 md:p-12 rounded-3xl border border-gray-100 shadow-xl">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-10">
              <div>
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-2">
                  Facilities services insights
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Metrics that anchor buyer conviction
                </h2>
              </div>
              <p className="text-sm md:text-base text-gray-600 max-w-xl">
                We quantify the KPIs that matter most: contract retention, labor efficiency, and ancillary service
                penetration. Buyers see where the upside lives before they request a plant tour.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {highlightStats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transition-transform duration-300 hover:-translate-y-1 flex flex-col gap-3 h-full"
                >
                  <div className="text-3xl sm:text-4xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amiva-primary">
                    {stat.label}
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Signals */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-4">
                Performance dashboard
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
                Show contract, workforce, and compliance health at a glance
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                Buyers move faster when they can underwrite retention and margin stability visually. These artifacts
                eliminate guesswork and keep diligence anchored to value creation.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {serviceSignals.map((signal) => (
                <div
                  key={signal.title}
                  className="group relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50 rounded-3xl border-2 border-orange-200 p-8 hover:border-orange-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-orange-200 rounded-full opacity-20 -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="relative">
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl mb-5 group-hover:scale-110 transition-transform duration-300">
                      {signal.icon}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{signal.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{signal.summary}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Readiness Checklist */}
      <section className="bg-gray-50 py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Seller Readiness Checklist</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              We transform this checklist into a coordinated sprint that protects your leadership bandwidth. Every item
              is resourced, sequenced, and captured inside a secure data room before buyers arrive.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {readinessChecklist.map((item) => (
              <div key={item} className="card-premium p-6 transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-start gap-3">
                  <span className="mt-2 inline-flex h-2.5 w-2.5 flex-none rounded-full bg-amiva-primary"></span>
                  <p className="text-gray-700 leading-relaxed">{item}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process & Buyer Universe */}
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-4">
                  Deal choreography
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  Our Process & Buyer Universe
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  From union environments to multi-state compliance, we keep diligence proactive and organized so the
                  right sponsors, strategics, and family offices lean in quickly.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {processHighlights.map((phase) => (
                  <div key={phase.title} className="card-premium p-6 border border-gray-100">
                    <div className="text-xs font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-3">
                      {phase.timeline}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">{phase.title}</h3>
                    <ul className="space-y-2">
                      {phase.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amiva-primary"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="card-gradient p-6 shadow-lg">
                <p className="text-sm text-gray-600">
                  Outcome snapshot: buyers model 25% cross-sell uplift, 72% cash at close, and structured rollovers for
                  leadership to capture second-exit upside.
                </p>
              </div>
            </div>
            <div className="card-gradient p-8 shadow-xl transition-all duration-300 hover:-translate-y-1">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Buyer Bench Highlights</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed">
                  <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-amiva-primary"></span>
                  <span>Global FM strategics layering janitorial onto hard services and workplace experience stacks.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed">
                  <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-amiva-primary"></span>
                  <span>PE platforms executing roll-ups of regional cleaning firms with robust ESG credentials.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed">
                  <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-amiva-primary"></span>
                  <span>Family offices focused on mission-critical verticals (healthcare, pharma, clean rooms).</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed">
                  <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-amiva-primary"></span>
                  <span>Utilities and energy companies acquiring cleaning platforms to expand ESG service offerings.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed">
                  <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-amiva-primary"></span>
                  <span>Hospitality and retail conglomerates seeking onsite staffing synergies across portfolios.</span>
                </li>
              </ul>
              <p className="text-sm text-gray-500 mt-6">
                We maintain scorecards on appetite, geographic focus, union readiness, and integration style so you only
                meet buyers positioned to honor your contracts and teams.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Link */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Related Playbooks
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Explore adjacent AMIVA industry coverage
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Many facilities operators branch into HVAC, manufacturing support services, or international roll-ups.
              These playbooks extend the same diligence discipline to those paths.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPlaybooks.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="group rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1 hover:border-amiva-primary/40"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  Playbook
                </span>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-amiva-primary">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{item.description}</p>
                <span className="text-sm font-semibold text-amiva-primary group-hover:text-amiva-accent inline-flex items-center gap-2">
                  View details
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 10.5L10.5 3.5M10.5 3.5H4.375M10.5 3.5V9.625"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-amiva-primary to-amiva-accent text-white py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">Ready to explore a facilities services exit?</h2>
            <p className="text-lg text-white/90">
              Let’s benchmark your contract portfolio, calibrate buyer fit, and stage a process that protects your
              people and clients.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/valuation" className="bg-white text-amiva-primary font-semibold px-6 py-3 rounded-xl text-center">
              Start your valuation
            </Link>
            <a
              href={appConfig.links.calendly}
              target="_blank"
              rel="noreferrer"
              className="border border-white/70 rounded-xl px-6 py-3 font-semibold text-center hover:bg-white/10 transition"
            >
              Book a strategy call
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
